import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { CampaignListItemDto } from '@shared/services/apiService';
import type { WorkspaceModule } from '@shared/designVariants/catalog';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useDesignWorkspaceDirty } from '@shared/designVariants/useDesignWorkspaceDirty';
import { AgentDirectoryWorkspace } from './AgentDirectoryWorkspace';
import { AssistantsPage } from '@pages/Admin/Assistants';
import { MissionsWorkspace } from './MissionsWorkspace';
import { AskWorkspace } from './AskWorkspace';
import { ConcurrencyWorkspace } from './ConcurrencyWorkspace';
import { CampaignPicker } from './CampaignPicker';
import { CompanyDashboard } from './CompanyDashboard';
import { CampaignResources } from './CampaignResources';
import { ConnectionsWorkspace } from './ConnectionsWorkspace';

export const WorkspaceDialog = ({
  module,
  onClose,
  embedded = false,
}: {
  module: WorkspaceModule;
  onClose: () => void;
  embedded?: boolean;
}) => {
  const { registerGuard } = useDesignWorkspace();
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down('sm'));
  const [campaign, setCampaign] = useState<CampaignListItemDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useDesignWorkspaceDirty(dirty);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const onDirty = useCallback((value: boolean) => setDirty(value), []);
  const request = useCallback(
    (action: () => void) => {
      if (dirty) setPending(() => action);
      else action();
    },
    [dirty],
  );
  useEffect(() => {
    if (!embedded) return;
    registerGuard(request);
    return () => registerGuard(null);
  }, [embedded, registerGuard, request]);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);
  const companyModule = [
    'Dashboard',
    'Concurrency',
    'Jobs',
    'Missions',
    'Ask Callevate',
    'Agents',
    'Connections',
  ].includes(module);
  const content = (
    <>
      {companyModule ? (
        module === 'Agents' ? (
          <AgentDirectoryWorkspace />
        ) : module === 'Connections' ? (
          <ConnectionsWorkspace onDirtyChange={onDirty} requestNavigation={request} />
        ) : module === 'Missions' ? (
          <MissionsWorkspace
            onDirtyChange={onDirty}
            requestNavigation={request}
            onClose={onClose}
          />
        ) : module === 'Ask Callevate' ? (
          <AskWorkspace onDirtyChange={onDirty} />
        ) : module === 'Concurrency' ? (
          <ConcurrencyWorkspace onDirtyChange={onDirty} requestNavigation={request} />
        ) : module === 'Jobs' ? (
          <>
            <Alert severity="info">
              Reusable instructions can be shared by multiple campaign agents. Review changes before
              saving.
            </Alert>
            <AssistantsPage embedded />
          </>
        ) : (
          <CompanyDashboard onDirtyChange={onDirty} requestNavigation={request} />
        )
      ) : (
        <>
          <CampaignPicker
            value={campaign}
            onChange={(next) =>
              request(() => {
                onDirty(false);
                setCampaign(next);
              })
            }
          />
          {campaign ? (
            <CampaignResources
              key={campaign.id}
              module={module}
              campaignId={campaign.id}
              contextSurface={module}
              onDirtyChange={onDirty}
              requestNavigation={request}
            />
          ) : (
            <Typography color="text.secondary">
              Select a campaign to view and manage its {module.toLowerCase()}.
            </Typography>
          )}
        </>
      )}
    </>
  );
  return (
    <>
      {embedded ? (
        <Box component="section" aria-label={`${module} workspace page`}>
          {!['Agents', 'Missions', 'Jobs'].includes(module) && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}>
              <Box>
                <Typography component="h1" variant="h3">
                  {module}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Manage your current company resources.
                </Typography>
              </Box>
              <Button onClick={() => request(onClose)}>Back to workspace</Button>
            </Stack>
          )}
          {content}
        </Box>
      ) : (
        <Dialog
          open
          onClose={() => request(onClose)}
          maxWidth="xl"
          fullWidth
          fullScreen={small}
          aria-labelledby="workspace-title">
          <DialogTitle id="workspace-title">{module}</DialogTitle>
          <DialogContent>{content}</DialogContent>
          <DialogActions>
            <Button onClick={() => request(onClose)}>Close workspace</Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog
        open={!!pending}
        onClose={() => setPending(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="workspace-dirty-title">
        <DialogTitle id="workspace-dirty-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          Your saved data stays unchanged. Unsaved changes in this workspace will be lost.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              const action = pending;
              setPending(null);
              setDirty(false);
              action?.();
            }}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
